import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { BarChart } from '@mui/x-charts/BarChart';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import {
  reconciliationApi,
  type BreakResolutionStatus,
  type ReconciliationBreak,
  type ReconciliationRun,
} from '../../api/reconciliation.api.js';
import { useAuth } from '../../auth/useAuth.js';
import { StatusBadge } from '../../components/StatusBadge.js';

const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

function formatCents(cents: string, currency: string): string {
  try {
    const value = Number(BigInt(cents)) / 100;
    return new Intl.NumberFormat('en-EU', {
      style: 'currency',
      currency,
    }).format(value);
  } catch {
    return `${cents} ${currency}`;
  }
}

function ageingColor(days: number): 'success' | 'warning' | 'error' {
  if (days < 1) return 'success';
  if (days <= 5) return 'warning';
  return 'error';
}

function ageingLabel(days: number): string {
  if (days < 1) return 'Fresh';
  if (days <= 5) return `${days}d`;
  return `${days}d (overdue)`;
}

export function ReconciliationDashboardPage(): JSX.Element {
  const queryClient = useQueryClient();
  // Finance reads reconciliation; resolving a break is AML / MLRO or admin work (DEV-67).
  const canResolve = useAuth().can('reconciliation.work');
  const [selectedRun, setSelectedRun] = useState<ReconciliationRun | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [activeBreak, setActiveBreak] = useState<ReconciliationBreak | null>(null);
  const [resolutionStatus, setResolutionStatus] =
    useState<BreakResolutionStatus>('RESOLVED');
  const [resolutionNotes, setResolutionNotes] = useState('');

  const statsQuery = useQuery({
    queryKey: ['recon-stats', 'DAILY'],
    queryFn: () => reconciliationApi.stats('DAILY'),
    refetchInterval: REFRESH_INTERVAL_MS,
  });

  const runsQuery = useQuery({
    queryKey: ['recon-runs', startDate, endDate],
    queryFn: () =>
      reconciliationApi.listRuns({
        ...(startDate ? { startDate } : {}),
        ...(endDate ? { endDate } : {}),
        limit: 20,
      }),
    refetchInterval: REFRESH_INTERVAL_MS,
  });

  const runDetailQuery = useQuery({
    queryKey: ['recon-run', selectedRun?.runId],
    queryFn: () =>
      selectedRun !== null
        ? reconciliationApi.getRun(selectedRun.runId)
        : Promise.resolve(null),
    enabled: selectedRun !== null,
  });

  const resolveMutation = useMutation({
    mutationFn: (args: {
      breakId: string;
      resolutionStatus: BreakResolutionStatus;
      resolutionNotes: string;
    }) =>
      reconciliationApi.resolveBreak(args.breakId, {
        resolutionStatus: args.resolutionStatus,
        resolutionNotes: args.resolutionNotes,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['recon-run'] });
      await queryClient.invalidateQueries({ queryKey: ['recon-runs'] });
      await queryClient.invalidateQueries({ queryKey: ['recon-stats'] });
      setActiveBreak(null);
      setResolutionNotes('');
    },
  });

  const trendData = useMemo(() => {
    const trend = statsQuery.data?.trend ?? [];
    return {
      x: trend.map((t) => t.date),
      y: trend.map((t) => t.matchRatePct),
    };
  }, [statsQuery.data]);

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Reconciliation Dashboard
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                Match rate (today)
              </Typography>
              <Typography variant="h5">
                {statsQuery.data
                  ? `${statsQuery.data.matchRatePct.toFixed(2)}%`
                  : '—'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                Open breaks
              </Typography>
              <Typography variant="h5">
                {statsQuery.data?.totalBreaks ?? '—'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                Total break amount
              </Typography>
              <Typography variant="h5">
                {statsQuery.data
                  ? formatCents(statsQuery.data.totalBreakCents, 'EUR')
                  : '—'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2">
                Auto-refresh
              </Typography>
              <Typography variant="h5">5 min</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Match rate trend (last 30 days)
          </Typography>
          {trendData.x.length > 0 ? (
            <BarChart
              height={240}
              xAxis={[{ data: trendData.x, scaleType: 'band', label: 'Date' }]}
              series={[{ data: trendData.y, label: 'Match rate %' }]}
            />
          ) : (
            <CircularProgress size={24} />
          )}
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">Reconciliation runs</Typography>
            <Box flex={1} />
            <TextField
              label="Start date"
              type="date"
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End date"
              type="date"
              size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Run date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Channel</TableCell>
                <TableCell align="right">Ledger net</TableCell>
                <TableCell align="right">Partner net</TableCell>
                <TableCell align="right">Break</TableCell>
                <TableCell align="right">Break count</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {runsQuery.data?.map((run) => (
                <TableRow
                  key={run.runId}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setSelectedRun(run)}
                >
                  <TableCell>{run.runDate.slice(0, 10)}</TableCell>
                  <TableCell>
                    <StatusBadge kind="recon" value={run.status} />
                  </TableCell>
                  <TableCell>{run.channel}</TableCell>
                  <TableCell align="right">
                    {formatCents(run.ledgerNetCents, run.currency)}
                  </TableCell>
                  <TableCell align="right">
                    {formatCents(run.partnerStatementNetCents, run.currency)}
                  </TableCell>
                  <TableCell align="right">
                    {formatCents(run.breakCents, run.currency)}
                  </TableCell>
                  <TableCell align="right">{run.breakCount}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Drawer
        anchor="right"
        open={selectedRun !== null}
        onClose={() => setSelectedRun(null)}
        PaperProps={{ sx: { width: { xs: '100%', md: 600 } } }}
      >
        <Box p={3}>
          <Typography variant="h6" gutterBottom>
            Run {selectedRun?.runId}
          </Typography>
          {runDetailQuery.isLoading ? (
            <CircularProgress />
          ) : runDetailQuery.data ? (
            <>
              <Typography variant="body2" color="text.secondary">
                {runDetailQuery.data.runDate.slice(0, 10)} · {runDetailQuery.data.status}
              </Typography>
              <Typography variant="subtitle1" sx={{ mt: 2 }}>
                Breaks ({runDetailQuery.data.breaks.length})
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Reference</TableCell>
                    <TableCell align="right">Expected</TableCell>
                    <TableCell align="right">Actual</TableCell>
                    <TableCell>Age</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {runDetailQuery.data.breaks.map((brk) => (
                    <TableRow key={brk.breakId}>
                      <TableCell>{brk.breakType}</TableCell>
                      <TableCell>{brk.referenceId}</TableCell>
                      <TableCell align="right">
                        {formatCents(brk.expectedCents, brk.currency)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCents(brk.actualCents, brk.currency)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={ageingLabel(brk.ageDays)}
                          color={ageingColor(brk.ageDays)}
                        />
                      </TableCell>
                      <TableCell>{brk.resolutionStatus}</TableCell>
                      <TableCell>
                        {canResolve ? <Button
                          size="small"
                          onClick={() => setActiveBreak(brk)}
                          disabled={brk.resolutionStatus === 'RESOLVED'}
                        >
                          Resolve
                        </Button> : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          ) : null}
        </Box>
      </Drawer>

      <Dialog
        open={activeBreak !== null}
        onClose={() => setActiveBreak(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Resolve break</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Status"
              value={resolutionStatus}
              onChange={(e) => setResolutionStatus(e.target.value as BreakResolutionStatus)}
            >
              <MenuItem value="RESOLVED">Resolved</MenuItem>
              <MenuItem value="INVESTIGATING">Investigating</MenuItem>
              <MenuItem value="ESCALATED">Escalated</MenuItem>
            </TextField>
            <TextField
              label="Resolution notes"
              value={resolutionNotes}
              onChange={(e) => setResolutionNotes(e.target.value)}
              multiline
              minRows={3}
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActiveBreak(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={resolutionNotes.trim().length === 0 || resolveMutation.isPending}
            onClick={() => {
              if (activeBreak !== null) {
                resolveMutation.mutate({
                  breakId: activeBreak.breakId,
                  resolutionStatus,
                  resolutionNotes,
                });
              }
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
